import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService, { LoginData, RegisterData, AuthResponse } from '../services/auth.service';

interface AuthContextType {
  user: any;
  isAuthenticated: boolean;
  login: (data: LoginData) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Check if user is already authenticated from localStorage
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('access_token');
      const storedRefreshToken = localStorage.getItem('refresh_token');
      
      if (storedUser && storedToken) {
        try {
          // Verify token is still valid by calling profile endpoint
          const userData = await authService.getProfile();
          setUser(userData);
        } catch (error) {
          // Token expired, try to refresh
          if (storedRefreshToken) {
            try {
              const refreshResponse = await authService.refreshToken(storedRefreshToken);
              authService.setTokens(refreshResponse.accessToken, storedRefreshToken);
              // Try to get profile again
              const userData = await authService.getProfile();
              setUser(userData);
            } catch (refreshError) {
              // Refresh failed, clear storage
              authService.clearTokens();
              setUser(null);
            }
          } else {
            // No refresh token, clear storage
            authService.clearTokens();
            setUser(null);
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (data: LoginData) => {
    try {
      const response: AuthResponse = await authService.login(data);
      console.log('🔍 AuthContext Debug - Login response:', response);
      console.log('🔍 AuthContext Debug - User object:', response.user);
      console.log('🔍 AuthContext Debug - User role:', response.user.roleName);
      
      // Store tokens and user
      authService.setTokens(response.accessToken, response.refreshToken);
      authService.setUser(response.user);
      
      // Get full user profile with permissions
      try {
        const profile = await authService.getProfile();
        console.log('🔍 AuthContext Debug - Profile response:', profile);
        authService.setUser(profile);
        setUser(profile);
        console.log('🔍 AuthContext Debug - User state set to profile:', profile);
      } catch (profileError) {
        console.warn('🔍 AuthContext Debug - Profile fetch failed, using login response:', profileError);
        // Fallback to login response if profile fails
        setUser(response.user);
        console.log('🔍 AuthContext Debug - User state set to login response:', response.user);
      }
    } catch (error) {
      console.error('🔍 AuthContext Debug - Login error:', error);
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    try {
      const response: AuthResponse = await authService.register(data);
      authService.setTokens(response.accessToken, response.refreshToken);
      authService.setUser(response.user);
      setUser(response.user);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    // Clear state first, then logout
    setUser(null);
    authService.logout();
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
